import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, FootnoteReferenceRun, Footer, PageNumber, LineRuleType, type ParagraphChild } from 'docx';
import {
  ydocToJSON,
  getParagraphScriptRolesForExport,
  isSceneHeadingParagraphText,
  type ExportScriptParagraphRole,
} from '@narrativebench/editor-core';
import type { Comment, ExportLayoutOptions, ExportOptions } from './export-utils';
import {
  getExportBodyFontSizePt,
  getExportFilename,
  getWordExportFontName,
  resolveExportAppendixContent,
  resolveExportDocumentJson,
} from './export-utils';
import {
  applyScriptRoleTextTransform,
  getMentionExportText,
  isScriptCharacterCueRole,
  isScriptDialogueRole,
  isScriptParentheticalRole,
  isScriptSceneHeadingRole,
  SCRIPT_DIALOGUE_INDENT_TWIPS,
  scriptCharacterCueSpacingBeforeTwips,
  scriptSceneHeadingSpacingTwips,
} from './export-script-format';
import {
  EXPORT_SCRIPT_STATS_FONT_SIZE_PT,
  EXPORT_SCRIPT_STATS_GREY,
  collectExportScriptStatsQueue,
  takeNextExportScriptStatsLine,
} from './export-script-stats';
import {
  attachPointCommentsToChildren,
  buildNativeWordCommentDefinitions,
  injectCommentRangesIntoTextSegments,
  type PendingNativeWordComment,
  type TextSegmentForCommentInjection,
} from './export-word-native-comments';

type ConvertTipTapToWordResult = {
  paragraphs: Paragraph[];
  footnoteMap: Map<string, number>;
  footnoteData: Array<{ id: string; content: Paragraph[] }>;
};

type WordConvertContext = {
  scriptRoles: ExportScriptParagraphRole[];
  paragraphIndexRef: { current: number };
  scriptStatsQueue: Array<string | null>;
  bodyFontSizePt: number;
};

const createScriptStatsParagraph = (
  statsLine: string,
  spacingAfterTwips: number,
): Paragraph =>
  new Paragraph({
    alignment: AlignmentType.LEFT,
    spacing: { before: 0, after: spacingAfterTwips },
    children: [
      new TextRun({
        text: statsLine,
        size: EXPORT_SCRIPT_STATS_FONT_SIZE_PT * 2,
        color: EXPORT_SCRIPT_STATS_GREY,
      }),
    ],
  });

const paragraphPlainTextForExport = (node: any): string => {
  if (!node.content || !Array.isArray(node.content)) {
    return '';
  }
  return node.content
    .map((child: any) => {
      if (child.type === 'text') {
        return child.text || '';
      }
      if (child.type === 'hardBreak') {
        return '\n';
      }
      return getMentionExportText(child);
    })
    .join('');
};

const alignmentForScriptRole = (role: ExportScriptParagraphRole) => {
  if (isScriptCharacterCueRole(role) || isScriptParentheticalRole(role)) {
    return AlignmentType.CENTER;
  }
  if (isScriptDialogueRole(role)) {
    return AlignmentType.LEFT;
  }
  return AlignmentType.JUSTIFIED;
};

const spacingForScriptRole = (
  role: ExportScriptParagraphRole,
  bodyFontSizePt: number,
  options?: { omitAfter?: boolean },
) => {
  if (isScriptSceneHeadingRole(role)) {
    const gap = scriptSceneHeadingSpacingTwips(bodyFontSizePt);
    return {
      before: gap,
      after: options?.omitAfter ? 0 : gap,
    };
  }
  if (!isScriptCharacterCueRole(role)) {
    return undefined;
  }
  return {
    before: scriptCharacterCueSpacingBeforeTwips(bodyFontSizePt),
  };
};

const convertTipTapToWord = (
  node: any,
  footnoteMap: Map<string, number> = new Map(),
  pendingNativeComments: PendingNativeWordComment[] = [],
  ctx?: WordConvertContext,
): ConvertTipTapToWordResult => {
  const paragraphs: Paragraph[] = [];
  const footnoteData: Array<{ id: string; content: Paragraph[] }> = [];
  let footnoteCounter = 1;

  if (node.type === 'doc') {
    if (node.content && Array.isArray(node.content)) {
      const footnotesNode = node.content.find((child: any) => child.type === 'footnotes');
      const mainContent = node.content.filter((child: any) => child.type !== 'footnotes');
      
      if (footnotesNode && footnotesNode.content) {
        console.log('Processing footnotes node with', footnotesNode.content.length, 'items');
        footnotesNode.content.forEach((footnoteItem: any, index: number) => {
          if (footnoteItem.type === 'footnote') {
            const footnoteDataId = footnoteItem.attrs?.['data-id'] || footnoteItem.attrs?.dataId;
            const footnoteId = footnoteItem.attrs?.id || `fn:${footnoteCounter}`;
            console.log('Processing footnote with ID:', footnoteId, 'data-id:', footnoteDataId, 'index:', index);
            const footnoteContent: Paragraph[] = [];
            
            if (footnoteItem.content) {
              footnoteItem.content.forEach((fnChild: any) => {
                if (fnChild.type === 'paragraph' && fnChild.content) {
                  const textRuns: TextRun[] = [];
                  fnChild.content.forEach((textNode: any) => {
                    if (textNode.type === 'text') {
                      textRuns.push(new TextRun({
                        text: textNode.text || '',
                        bold: textNode.marks?.some((m: any) => m.type === 'bold'),
                        italics: textNode.marks?.some((m: any) => m.type === 'italic'),
                      }));
                    } else {
                      const mentionText = getMentionExportText(textNode);
                      if (mentionText) {
                        textRuns.push(new TextRun({ text: mentionText }));
                      }
                    }
                  });
                  if (textRuns.length > 0) {
                    footnoteContent.push(new Paragraph({
                      alignment: AlignmentType.LEFT,
                      children: textRuns,
                    }));
                  }
                }
              });
            }
            
            if (footnoteContent.length > 0) {
              footnoteData.push({
                id: footnoteDataId || footnoteId,
                content: footnoteContent,
              });
              
              if (footnoteDataId) {
                footnoteMap.set(footnoteDataId, footnoteCounter);
                console.log('Mapped footnote data-id:', footnoteDataId, 'to number:', footnoteCounter);
              }
              if (footnoteId) {
                footnoteMap.set(footnoteId, footnoteCounter);
              }
              footnoteCounter++;
            } else {
              console.warn('Footnote has no content:', footnoteId);
            }
          }
        });
      }
      
      mainContent.forEach((child: any) => {
        const result = convertTipTapToWord(child, footnoteMap, pendingNativeComments, ctx);
        paragraphs.push(...result.paragraphs);
        footnoteData.push(...result.footnoteData);
      });
    }
  } else if (node.type === 'paragraph') {
    const children: ParagraphChild[] = [];
    const textSegments: TextSegmentForCommentInjection[] = [];
    let hasContent = false;
    const scriptRole: ExportScriptParagraphRole =
      ctx?.scriptRoles[ctx.paragraphIndexRef.current++] ?? 'body';

    const flushTextSegments = (): void => {
      if (textSegments.length === 0) {
        return;
      }
      const injected = injectCommentRangesIntoTextSegments(textSegments, pendingNativeComments);
      children.push(...injected.children);
      pendingNativeComments.length = 0;
      pendingNativeComments.push(...injected.remaining);
      textSegments.length = 0;
    };

    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((child: any) => {
        if (child.type === 'text') {
          textSegments.push({
            text: applyScriptRoleTextTransform(child.text || '', scriptRole),
            bold: child.marks?.some((m: any) => m.type === 'bold'),
            italics: child.marks?.some((m: any) => m.type === 'italic'),
            underline: child.marks?.some((m: any) => m.type === 'underline'),
          });
          hasContent = true;
        } else if (child.type === 'hardBreak') {
          textSegments.push({ text: '\n' });
          hasContent = true;
        } else if (child.type === 'mention' || child.type === 'scriptMention') {
          const mentionText = applyScriptRoleTextTransform(getMentionExportText(child), scriptRole);
          if (mentionText) {
            textSegments.push({ text: mentionText });
            hasContent = true;
          }
        } else if (child.type === 'footnoteReference') {
          flushTextSegments();
          const footnoteDataId = child.attrs?.['data-id'] || child.attrs?.dataId;
          const referenceNumber = child.attrs?.referenceNumber;

          if (footnoteDataId && footnoteMap.has(footnoteDataId)) {
            const footnoteNumber = footnoteMap.get(footnoteDataId)!;
            children.push(new FootnoteReferenceRun(footnoteNumber));
            hasContent = true;
          } else if (referenceNumber) {
            const num = parseInt(referenceNumber, 10);
            if (!isNaN(num)) {
              children.push(new FootnoteReferenceRun(num));
              hasContent = true;
            }
          } else {
            console.warn('Footnote reference has no data-id or referenceNumber:', child.attrs);
          }
        }
      });
    }

    flushTextSegments();

    if (hasContent || children.length === 0) {
      const isSceneHeading =
        isScriptSceneHeadingRole(scriptRole)
        || isSceneHeadingParagraphText(paragraphPlainTextForExport(node));
      const effectiveRole: ExportScriptParagraphRole = isSceneHeading
        ? 'scene_heading'
        : scriptRole;
      const statsLine =
        isSceneHeading && ctx
          ? takeNextExportScriptStatsLine(ctx.scriptStatsQueue)
          : null;
      paragraphs.push(new Paragraph({
        alignment: alignmentForScriptRole(effectiveRole),
        spacing: spacingForScriptRole(effectiveRole, ctx?.bodyFontSizePt ?? 12, {
          omitAfter: Boolean(statsLine),
        }),
        indent: isScriptDialogueRole(effectiveRole)
          ? { left: SCRIPT_DIALOGUE_INDENT_TWIPS, right: SCRIPT_DIALOGUE_INDENT_TWIPS }
          : undefined,
        children: children.length > 0 ? children : [new TextRun('')],
      }));
      if (statsLine) {
        paragraphs.push(
          createScriptStatsParagraph(
            statsLine,
            scriptSceneHeadingSpacingTwips(ctx?.bodyFontSizePt ?? 12),
          ),
        );
      }
    }
  } else if (node.type === 'heading') {
    const level = node.attrs?.level || 1;
    const textSegments: TextSegmentForCommentInjection[] = [];

    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((child: any) => {
        if (child.type === 'text') {
          textSegments.push({
            text: child.text || '',
            bold: true,
          });
        } else if (child.type === 'mention' || child.type === 'scriptMention') {
          const mentionText = getMentionExportText(child);
          if (mentionText) {
            textSegments.push({ text: mentionText, bold: true });
          }
        }
      });
    }

    const injected = injectCommentRangesIntoTextSegments(textSegments, pendingNativeComments);
    pendingNativeComments.length = 0;
    pendingNativeComments.push(...injected.remaining);

    const headingLevel = level === 1 ? HeadingLevel.HEADING_1 :
                        level === 2 ? HeadingLevel.HEADING_2 :
                        level === 3 ? HeadingLevel.HEADING_3 :
                        level === 4 ? HeadingLevel.HEADING_4 :
                        level === 5 ? HeadingLevel.HEADING_5 :
                        HeadingLevel.HEADING_6;

    const statsLine = ctx ? takeNextExportScriptStatsLine(ctx.scriptStatsQueue) : null;
    paragraphs.push(new Paragraph({
      heading: headingLevel,
      alignment: AlignmentType.JUSTIFIED,
      spacing: statsLine
        ? { after: 0 }
        : undefined,
      children: injected.children.length > 0 ? injected.children : [new TextRun('')],
    }));
    if (statsLine) {
      paragraphs.push(
        createScriptStatsParagraph(
          statsLine,
          scriptSceneHeadingSpacingTwips(ctx?.bodyFontSizePt ?? 12),
        ),
      );
    }
  } else if (node.type === 'bulletList' || node.type === 'orderedList') {
    if (node.content && Array.isArray(node.content)) {
      node.content.forEach((listItem: any) => {
        if (listItem.type === 'listItem' && listItem.content) {
          listItem.content.forEach((itemContent: any) => {
            const result = convertTipTapToWord(itemContent, footnoteMap, pendingNativeComments, ctx);
            paragraphs.push(...result.paragraphs);
            footnoteData.push(...result.footnoteData);
          });
        }
      });
    }
  } else if (node.content && Array.isArray(node.content)) {
    node.content.forEach((child: any) => {
      const result = convertTipTapToWord(child, footnoteMap, pendingNativeComments, ctx);
      paragraphs.push(...result.paragraphs);
      footnoteData.push(...result.footnoteData);
    });
  }

  return { paragraphs, footnoteMap, footnoteData };
};

export const exportToWord = async (options: ExportOptions): Promise<void> => {
  const { editor, ydoc, documentTitle, projectTitle, documentId, token, apiBase, exportComments, exportSuggestions, includeCoverPage, coverAuthorName, comments, suggestions, t } = options;

  if (!editor) return;

  try {
    const defaultExportLayoutOptions: ExportLayoutOptions = {
      pageNumberPosition: 'bottom_right',
      lineSpacing: 1.5,
      marginsCm: {
        top: 2.5,
        right: 2.5,
        bottom: 2.5,
        left: 2.5,
      },
    };
    const exportLayoutOptions = options.exportLayoutOptions ?? options.wordExportOptions ?? defaultExportLayoutOptions;
    const bodyFontSizePt = getExportBodyFontSizePt(exportLayoutOptions.exportFontSizePt);

    const cmToTwips = (cm: number): number => Math.round((cm / 2.54) * 1440);

    const sectionProperties: Record<string, unknown> = {
      page: {
        margin: {
          top: cmToTwips(exportLayoutOptions.marginsCm.top),
          right: cmToTwips(exportLayoutOptions.marginsCm.right),
          bottom: cmToTwips(exportLayoutOptions.marginsCm.bottom),
          left: cmToTwips(exportLayoutOptions.marginsCm.left),
        },
      },
    };

    const pageNumberAlignment =
      exportLayoutOptions.pageNumberPosition === 'bottom_left'
        ? AlignmentType.LEFT
        : exportLayoutOptions.pageNumberPosition === 'bottom_middle'
          ? AlignmentType.CENTER
          : exportLayoutOptions.pageNumberPosition === 'bottom_right'
            ? AlignmentType.RIGHT
            : null;

    const sectionFooters =
      pageNumberAlignment === null
        ? undefined
        : {
            default: new Footer({
              children: [
                new Paragraph({
                  alignment: pageNumberAlignment,
                  children: [new TextRun({ children: [PageNumber.CURRENT] })],
                }),
              ],
            }),
          };

    const lineSpacing =
      typeof exportLayoutOptions.lineSpacing === 'number'
        ? {
            line: Math.round(240 * exportLayoutOptions.lineSpacing),
            lineRule: LineRuleType.AUTO,
          }
        : undefined;

    const json = resolveExportDocumentJson(editor, ydoc, ydocToJSON);
    if (!json) return;
    console.log('Document JSON structure:', JSON.stringify(json, null, 2).substring(0, 1000));

    const appendix = resolveExportAppendixContent({
      exportComments,
      exportSuggestions,
      comments,
      suggestions,
    });

    const commentsForNativeExport: Comment[] = appendix.comments;
    const pendingNativeComments: PendingNativeWordComment[] = commentsForNativeExport.map((comment, index) => ({
      id: index,
      selectedText: comment.selectedText || '',
    }));
    const nativeWordCommentDefinitions = buildNativeWordCommentDefinitions(
      commentsForNativeExport,
      t('reply'),
    );

    const scriptRoles = getParagraphScriptRolesForExport(editor.state.doc);
    const scriptStatsQueue = collectExportScriptStatsQueue(editor.state.doc, t);
    const expectedParagraphCount = (() => {
      const countParagraphs = (node: any): number => {
        if (node.type === 'paragraph') {
          return 1;
        }
        if (!node.content || !Array.isArray(node.content)) {
          return 0;
        }
        return node.content.reduce((sum: number, child: any) => {
          if (child.type === 'footnotes') {
            return sum;
          }
          return sum + countParagraphs(child);
        }, 0);
      };
      return countParagraphs(json);
    })();
    const { paragraphs, footnoteMap, footnoteData } = convertTipTapToWord(json, new Map(), pendingNativeComments, {
      scriptRoles: scriptRoles.length === expectedParagraphCount ? scriptRoles : [],
      paragraphIndexRef: { current: 0 },
      scriptStatsQueue,
      bodyFontSizePt,
    });
    console.log('Total paragraphs:', paragraphs.length, 'Footnotes:', footnoteData.length, 'Map size:', footnoteMap.size);

    if (pendingNativeComments.length > 0) {
      paragraphs.push(
        new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: attachPointCommentsToChildren([new TextRun('')], pendingNativeComments),
        }),
      );
      pendingNativeComments.length = 0;
    }

    if (appendix.suggestions.length > 0) {
      paragraphs.push(new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        children: [new TextRun({ text: '', break: 2 })],
      }));
      paragraphs.push(new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        children: [new TextRun({ text: t('suggestions'), bold: true, size: 20 })],
        heading: HeadingLevel.HEADING_1,
      }));
      appendix.suggestions.forEach((suggestion, index) => {
        paragraphs.push(new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [new TextRun({ text: `${index + 1}. `, bold: true })],
        }));
        paragraphs.push(new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [new TextRun({ text: `${t('original_text')}: `, bold: true }), new TextRun({ text: suggestion.originalText })],
        }));
        paragraphs.push(new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [new TextRun({ text: `${t('suggestion')}: `, bold: true }), new TextRun({ text: suggestion.suggestionText })],
        }));
        paragraphs.push(new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [new TextRun({ text: `${t('type')}: `, bold: true }), new TextRun({ text: suggestion.issueType === 'orthographic' ? t('orthographic') : suggestion.issueType === 'strength' ? t('strength') : t('other') })],
        }));
        paragraphs.push(new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [new TextRun({ text: `${t('suggestion_label')}: `, bold: true }), new TextRun({ text: suggestion.feedbackText })],
        }));
        paragraphs.push(new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [new TextRun({ text: `${t('user')}: ${t('automatic_analysis')}`, italics: true, size: 20 })],
        }));
        paragraphs.push(new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [new TextRun({ text: '' })],
        }));
      });
    }

    const footnoteRecords: Record<string, { children: Paragraph[] }> = {};
    footnoteData.forEach((fn) => {
      const fnNumber = footnoteMap.get(fn.id);
      if (fnNumber && fn.content && fn.content.length > 0) {
        footnoteRecords[fnNumber.toString()] = {
          children: fn.content,
        };
        console.log('Added footnote record:', fnNumber, 'with', fn.content.length, 'paragraphs');
      } else {
        console.warn('Skipping footnote:', fn.id, 'number:', fnNumber, 'content length:', fn.content?.length);
      }
    });

    if (Object.keys(footnoteRecords).length > 0) {
      console.log('Creating document with footnotes:', Object.keys(footnoteRecords).length, 'footnotes');
      console.log('Footnote records keys:', Object.keys(footnoteRecords));
    } else {
      console.warn('No footnote records created! footnoteData:', footnoteData, 'footnoteMap:', Array.from(footnoteMap.entries()));
    }

    let documentParagraphs = paragraphs;
    if (includeCoverPage) {
      const coverName =
        documentTitle && documentTitle.trim().length > 0 ? documentTitle.trim() : 'Proyecto';
      const authorOnCover = coverAuthorName && coverAuthorName.trim().length > 0 ? coverAuthorName.trim() : undefined;
      const coverLead: Paragraph[] = [
        new Paragraph({
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { before: 7200, after: authorOnCover ? 400 : 1200 },
          children: [new TextRun({ text: coverName, bold: true })],
        }),
      ];
      if (authorOnCover) {
        coverLead.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 1200 },
            children: [new TextRun({ text: authorOnCover, size: 24 })],
          }),
        );
      }
      coverLead.push(
        new Paragraph({
          children: [new TextRun({ text: '', break: 2 })],
        }),
      );
      documentParagraphs = [...coverLead, ...paragraphs];
    }

    const wordFontName = getWordExportFontName(exportLayoutOptions.exportFontId);
    const defaultDocument: Record<string, unknown> = {};
    if (lineSpacing) {
      defaultDocument.paragraph = { spacing: lineSpacing };
    }
    const defaultRun: Record<string, unknown> = {
      size: bodyFontSizePt * 2,
    };
    if (wordFontName) {
      defaultRun.font = wordFontName;
    }
    defaultDocument.run = defaultRun;
    const docOptions: any = {
      ...(Object.keys(defaultDocument).length > 0
        ? { styles: { default: { document: defaultDocument } } }
        : {}),
      sections: [{
        properties: sectionProperties,
        ...(sectionFooters ? { footers: sectionFooters } : {}),
        children:
          documentParagraphs.length > 0
            ? documentParagraphs
            : [new Paragraph({ alignment: AlignmentType.JUSTIFIED, children: [new TextRun('')] })],
      }],
    };

    if (Object.keys(footnoteRecords).length > 0) {
      docOptions.footnotes = footnoteRecords;
      console.log('Adding footnotes to document:', Object.keys(footnoteRecords));
    }

    if (nativeWordCommentDefinitions.length > 0) {
      docOptions.comments = {
        children: nativeWordCommentDefinitions,
      };
    }

    const doc = new Document(docOptions);

    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = getExportFilename(documentTitle, projectTitle, 'docx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    if (token) {
      try {
        await fetch(`${apiBase}/api/exports`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            documentId: documentId || null,
            exportType: 'word',
            documentTitle: documentTitle || null,
          }),
        });
      } catch (err) {
        console.error('Error tracking export:', err);
      }
    }
  } catch (err) {
    console.error('Error exporting to Word:', err);
    alert('Error al exportar el proyecto. Por favor, inténtalo de nuevo.');
  }
};

